import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, Send, RefreshCw, ShieldCheck, AlertTriangle, CheckCircle2, XCircle, RotateCcw, Users } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import { TextF, SelectF, inputCls } from "./AFields";

const STEPS = [
  { key: "ai_created", label: "AI CREATED" },
  { key: "image_ready", label: "IMAGE READY" },
  { key: "template_match_check", label: "TEMPLATE MATCH CHECK" },
  { key: "template_decision", label: "TEMPLATE" },
  { key: "submitted", label: "SUBMITTED" },
  { key: "pending", label: "PENDING" },
  { key: "approved", label: "APPROVED" },
  { key: "media_verified", label: "MEDIA + VARIABLE VERIFIED" },
  { key: "recipients_ready", label: "RECIPIENTS READY" },
  { key: "ready_for_broadcast", label: "READY FOR BROADCAST" },
  { key: "sending", label: "SEND BROADCAST" },
  { key: "completed", label: "COMPLETED" },
];

const STAGE_INDEX = { ai_created: 0, image_ready: 1, template_match_check: 2, template_reused: 3,
  meta_template_created: 3, submitted: 4, pending: 5, approved: 6, rejected: 6, review_required: 6,
  media_verified: 7, recipients_ready: 8, ready_for_broadcast: 9, sending: 10, completed: 11, failed: -1, created: -1 };

const TERMINAL = ["ready_for_broadcast", "completed", "failed", "review_required"];

const CHECK_LABELS = { greeting_exists: "Greeting exists", greeting_version_frozen: "Greeting version frozen",
  image_matches_greeting: "Current image bound to greeting", image_url_ok: "Public image URL works",
  template_approved: "Template APPROVED", media_dynamic: "Dynamic media header",
  media_variable_mapped: "Media variable mapped", body_variables_resolve: "Body variables resolve",
  provider_healthy: "Provider healthy" };

const bannerTone = { amber: "border-amber-300 bg-amber-50 text-amber-800", blue: "border-blue-300 bg-blue-50 text-blue-800", red: "border-red-300 bg-red-50 text-red-700" };

function Stepper({ pipe }) {
  const idx = STAGE_INDEX[pipe?.stage] ?? -1;
  const failed = pipe?.stage === "failed";
  const rejected = ["rejected", "review_required"].includes(pipe?.stage);
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="pipeline-stage-stepper">
      {STEPS.map((s, i) => {
        let label = s.label;
        if (s.key === "template_decision") label = pipe?.template_reused ? "APPROVED TEMPLATE REUSED" : "META TEMPLATE CREATED";
        const done = idx > i || (idx === i && ["completed"].includes(pipe?.stage));
        const active = idx === i && !["completed"].includes(pipe?.stage);
        const err = (failed && idx === -1 && i === 0) || (rejected && i === 6);
        return (
          <div key={s.key} className="flex items-center gap-1">
            <span data-testid={`pipeline-step-${s.key}`}
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold tracking-wide ${err ? "bg-red-100 text-red-700" : done ? "bg-emerald-100 text-emerald-700" : active ? "bg-saffron-100 text-saffron-700 ring-1 ring-saffron-400" : "bg-slate-100 text-slate-400"}`}>
              {err ? <XCircle size={10} className="mr-1 inline" /> : done ? <CheckCircle2 size={10} className="mr-1 inline" /> : null}{label}
            </span>
            {i < STEPS.length - 1 && <span className="text-slate-300">→</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function PipelineTab() {
  const [cfg, setCfg] = useState(null);
  const [pipes, setPipes] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ prompt: "", language: "bilingual", occasion: "", date: "" });
  const [busy, setBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const pollRef = useRef(null);

  const loadList = () => api.get("/pipeline/list").then((r) => setPipes(r.data)).catch(() => {});
  const loadDetail = (id) => api.get(`/pipeline/${id}`).then((r) => setActive(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/pipeline/config").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e)));
    loadList();
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (active && !TERMINAL.includes(active.stage)) {
      pollRef.current = setInterval(() => loadDetail(active.id), 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [active?.id, active?.stage]); // eslint-disable-line

  const createPipeline = async () => {
    if (!form.prompt.trim()) { toast.error("Enter a prompt, e.g. 'Create Diwali greeting for our donors'"); return; }
    setBusy(true);
    try {
      const r = await api.post("/pipeline/create", { ...form, auto: true });
      toast.success("Pipeline started — AI is generating");
      setActive(r.data);
      loadList();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const pollStatus = async () => {
    try { const r = await api.post(`/pipeline/${active.id}/poll`); setActive(r.data); loadList(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const retryTemplate = async () => {
    try { const r = await api.post(`/pipeline/${active.id}/retry`); setActive(r.data); toast.success("Corrected template resubmitted"); loadList(); }
    catch (e) { toast.error(errMsg(e)); loadDetail(active.id); }
  };

  const sendBroadcast = async () => {
    if (!window.confirm(`SEND BROADCAST to ${active.recipients_valid} recipients?\n\nThis is the one irreversible human action. The greeting, image, template and recipient list will be frozen into an immutable job.`)) return;
    setSendBusy(true);
    try {
      const r = await api.post(`/pipeline/${active.id}/send-broadcast`);
      toast.success(r.data.simulated ? `Broadcast simulated for ${r.data.recipients} recipients (mock mode — nothing sent)` : `Broadcast started for ${r.data.recipients} recipients`);
      setActive(r.data.pipeline);
      loadList();
    } catch (e) { toast.error(errMsg(e)); loadDetail(active.id); } finally { setSendBusy(false); }
  };

  const setMockOutcome = async (v) => {
    try { const r = await api.put("/pipeline/config", { mock_meta_outcome: v }); setCfg({ ...cfg, ...r.data }); toast.success(`Mock Meta outcome: ${v}`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-6 space-y-6" data-testid="pipeline-tab">
      {cfg && (
        <div className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-xs font-bold ${bannerTone[cfg.tone] || bannerTone.amber}`} data-testid="pipeline-banner">
          <span><ShieldCheck size={14} className="mr-1.5 inline" />{cfg.banner}</span>
          <span className="font-semibold">AI model: {cfg.ai_model} · Auto-prep: {cfg.auto_prep ? "ON" : "OFF"} · Max Meta retries: {cfg.meta_max_retries}</span>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-5">
          <p className="flex items-center gap-2 text-sm font-bold text-navy"><Sparkles size={15} className="text-saffron-600" /> One-Prompt Broadcast Pipeline</p>
          <textarea rows={3} value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })}
            placeholder="e.g. Create a Diwali greeting for our donors and volunteers"
            className={inputCls} data-testid="pipeline-prompt" />
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectF label="Language" value={form.language} onChange={(v) => setForm({ ...form, language: v })} options={["bilingual", "english", "hindi"]} />
            <TextF label="Date (optional)" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
          </div>
          <TextF label="Occasion (optional — AI can derive it)" value={form.occasion} onChange={(v) => setForm({ ...form, occasion: v })} />
          <button onClick={createPipeline} disabled={busy} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm disabled:opacity-50" data-testid="pipeline-create-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Create Pipeline
          </button>
          <p className="text-[11px] leading-relaxed text-slate-400">Runs automatically up to READY FOR BROADCAST. Sending always requires your click. The scheduler uses the same pipeline and can never send.</p>
          {cfg?.mode === "mock" && (
            <div className="rounded-md bg-slate-50 p-3 text-[11px] text-slate-500">
              <p className="font-bold text-slate-600">Mock testing control</p>
              <div className="mt-1.5 flex gap-2">
                {["approved", "rejected"].map((v) => (
                  <button key={v} onClick={() => setMockOutcome(v)} data-testid={`mock-outcome-${v}`}
                    className={`rounded-full px-3 py-1 text-[11px] font-bold ${cfg.mock_meta_outcome === v ? "bg-navy text-white" : "border border-gray-200 bg-white text-navy"}`}>
                    Meta → {v.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="xl:col-span-2 space-y-3">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pipelines</p>
          {pipes.length === 0 && <p className="rounded-lg border border-dashed border-gray-200 py-10 text-center text-sm text-slate-400" data-testid="pipeline-empty">No pipelines yet — create one from a single prompt.</p>}
          {pipes.map((p) => (
            <button key={p.id} onClick={() => loadDetail(p.id)} data-testid={`pipeline-card-${p.id}`}
              className={`block w-full rounded-lg border bg-white p-4 text-left transition-colors ${active?.id === p.id ? "border-saffron-400" : "border-gray-200 hover:border-saffron-300"}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-bold text-navy">{p.occasion || p.original_prompt.slice(0, 60)}</p>
                <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${p.stage === "ready_for_broadcast" ? "bg-emerald-100 text-emerald-700" : p.stage === "completed" ? "bg-navy text-white" : ["failed", "rejected", "review_required"].includes(p.stage) ? "bg-red-100 text-red-700" : "bg-saffron-100 text-saffron-700"}`}
                  data-testid={`pipeline-stage-${p.id}`}>{String(p.stage).replaceAll("_", " ")}</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">{p.date || "—"} · {p.recipients_valid || 0} recipients · {p.template_reused ? "template reused" : p.template_name || "no template yet"} · {String(p.created_at).slice(0, 16).replace("T", " ")}</p>
            </button>
          ))}
        </div>
      </div>

      {active && (
        <div className="space-y-5 rounded-lg border border-gray-200 bg-white p-6" data-testid="pipeline-detail">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-bold text-navy">Pipeline {active.id.slice(0, 8)} — {active.occasion || "Greeting"}</p>
            <button onClick={() => loadDetail(active.id)} className="rounded-md border border-gray-200 p-2 text-navy hover:border-saffron-500" aria-label="Refresh" data-testid="pipeline-refresh-btn"><RefreshCw size={14} /></button>
          </div>
          <Stepper pipe={active} />

          {active.stage === "failed" && (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-xs font-semibold text-red-700" data-testid="pipeline-error"><AlertTriangle size={13} className="mr-1 inline" />{active.error || "Pipeline failed"}</p>
          )}
          {["rejected", "review_required"].includes(active.stage) && (
            <div className="rounded-lg bg-red-50 px-4 py-3" data-testid="pipeline-rejection">
              <p className="text-xs font-bold text-red-700">META REJECTED — {active.rejection_reason || "reason unavailable"}</p>
              {active.stage === "review_required"
                ? <p className="mt-1 text-xs font-bold text-red-800">SUPERADMIN REVIEW REQUIRED — retry limit ({cfg?.meta_max_retries}) reached.</p>
                : <button onClick={retryTemplate} className="btn-dark mt-2 !rounded-md !px-4 !py-2 text-xs" data-testid="pipeline-retry-btn"><RotateCcw size={12} /> AI-Correct & Resubmit (attempt {active.retry_count + 1} of {cfg?.meta_max_retries})</button>}
            </div>
          )}

          <div className="grid gap-5 lg:grid-cols-2">
            {active.greeting && (
              <div className="rounded-lg bg-navy-50/50 p-4" data-testid="pipeline-greeting-preview">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Generated Greeting (draft, v{active.greeting.version || 1})</p>
                {active.greeting.image && <img src={active.greeting.image} alt="" className="mt-2 aspect-square w-full max-w-[220px] rounded-md object-cover" />}
                <p className="mt-2 text-sm font-bold text-navy">{active.greeting.title}</p>
                <p className="mt-1 whitespace-pre-wrap text-xs text-slate-600">{active.greeting.message}</p>
                {active.greeting.message_hi && <p className="mt-1 whitespace-pre-wrap text-xs text-slate-600">{active.greeting.message_hi}</p>}
                <p className="mt-2 rounded bg-white px-2 py-1.5 text-xs text-slate-500">WA: {active.greeting.whatsapp_message}</p>
              </div>
            )}
            <div className="space-y-3">
              {active.template && (
                <div className="rounded-lg bg-navy-50/50 p-4 text-xs" data-testid="pipeline-template-info">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Template</p>
                  <p className="mt-1 font-bold text-navy">{active.template.name} {active.template_reused && <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] text-emerald-700">REUSED</span>}</p>
                  <p className="mt-0.5 text-slate-500">Content SID: {active.content_sid || "—"} · Status: {active.template.status} · Header: {active.header_type}{active.media_dynamic ? " (dynamic media)" : ""}</p>
                  {active.static_header_conflict && <p className="mt-1 font-bold text-amber-700">TEMPLATE NOT REUSABLE — STATIC MEDIA HEADER (new template created)</p>}
                </div>
              )}
              <div className="rounded-lg bg-navy-50/50 p-4 text-xs" data-testid="pipeline-recipients-info">
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500"><Users size={11} className="mr-1 inline" />Recipients</p>
                <p className="mt-1 text-slate-600">{active.recipients_valid} valid · {active.recipients_excluded} excluded (duplicates / opt-out / invalid) · {active.recipients_total} total in book</p>
              </div>
              {active.checks && (
                <div className="rounded-lg bg-navy-50/50 p-4" data-testid="pipeline-checks">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Broadcast Gate Checks</p>
                  <div className="mt-2 grid grid-cols-2 gap-1.5">
                    {Object.entries(active.checks).map(([k, v]) => (
                      <p key={k} className={`text-[11px] font-semibold ${v ? "text-emerald-700" : "text-red-600"}`} data-testid={`pipeline-check-${k}`}>
                        {v ? <CheckCircle2 size={11} className="mr-1 inline" /> : <XCircle size={11} className="mr-1 inline" />}{CHECK_LABELS[k] || k}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {active.history?.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-100 p-3" data-testid="pipeline-history">
              {active.history.slice().reverse().map((h, i) => (
                <p key={i} className="text-[11px] text-slate-500"><span className="font-bold text-navy">{String(h.stage).replaceAll("_", " ").toUpperCase()}</span> · {String(h.at).slice(0, 19).replace("T", " ")} {h.note ? `— ${h.note}` : ""}</p>
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
            {["submitted", "pending"].includes(active.stage) && (
              <button onClick={pollStatus} className="btn-dark !rounded-md !px-5 !py-2.5 text-sm" data-testid="pipeline-poll-btn"><RefreshCw size={14} /> Check Meta Status</button>
            )}
            <button onClick={sendBroadcast} disabled={active.stage !== "ready_for_broadcast" || sendBusy}
              className="btn-primary !rounded-md !px-6 !py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-40" data-testid="send-broadcast-btn">
              {sendBusy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} SEND BROADCAST
            </button>
            {active.stage !== "ready_for_broadcast" && active.stage !== "completed" && (
              <p className="text-[11px] text-slate-400">SEND BROADCAST activates only at READY FOR BROADCAST after all gate checks pass.</p>
            )}
            {active.stage === "completed" && <p className="text-xs font-bold text-emerald-700" data-testid="pipeline-completed-note"><CheckCircle2 size={13} className="mr-1 inline" />Broadcast job {active.broadcast_job_id?.slice(0, 8)} completed{cfg?.mode === "mock" ? " (simulated — nothing was sent)" : ""}.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
